import React, { useEffect, useRef, useLayoutEffect, useState } from "react";
import "./NewMessages.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useMessengerContext } from "../../hooks/useMessengerContext";
import Message from "./CopyMessage";
import TypingIndicator from "./TypingIndicator";

const Messages = ({ onToChatProfileForCSS, onToSuggestionProfileForCSS }) => {
  const containerRef = useRef(null);
  const { state, dispatch } = useMessengerContext();
  const { user } = useAuthContext();
  
  // Force minimum display time for typing indicators
  const [forceShowTyping, setForceShowTyping] = useState({});
  // Simple scroll-to-bottom button state
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const lastReadRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const typingIndicatorRef = useRef(null);
  const initialScrollDone = useRef(false);
  const currentConversationId = useRef(null);
  const scrollTimeout = useRef(null);
  const resizeObserver = useRef(null);
  const lastKnownHeight = useRef(0);

  const {
    messages,
    otherUserDisplay,
    community,
    userDisplayProfile,
    currentConversation,
    lastReadMessageId,
    typingUsers,
    typingBots,
  } = state;

  useEffect(() => {
  initialScrollDone.current = true;
  }, []);


  // Track lastReadMessageId changes for debugging if needed
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('LastRead updated:', lastReadMessageId, 'for conversation:', currentConversation?._id);
    }
  }, [lastReadMessageId, currentConversation?._id]);

  // Get typing indicators for current conversation (moved up before useEffects)
  const currentTypingUsers = typingUsers[currentConversation?._id] || {};
  const currentTypingBots = typingBots[currentConversation?._id] || {};

  // Removed: Clear typing state when conversation changes - was interfering with welcome flow typing indicators

  // Force minimum display time when new typing users appear
  useEffect(() => {
    const newTypingUsers = Object.keys(currentTypingUsers).filter(userId => userId !== user.userId);
    newTypingUsers.forEach(userId => {
      if (!forceShowTyping[userId]) {
        setForceShowTyping(prev => ({
          ...prev,
          [userId]: Date.now()
        }));
      }
    });
  }, [currentTypingUsers, user.userId]);

  // Reset scroll state when conversation changes
  useEffect(() => {
    if (currentConversation?._id !== currentConversationId.current) {
      currentConversationId.current = currentConversation?._id;
      initialScrollDone.current = false;
      
      // Clear any pending scroll timeouts
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = null;
      }
    }
  }, [currentConversation?._id]);

  // Enhanced scroll to last read message with improved AI bot message handling
  useLayoutEffect(() => {
    if (initialScrollDone.current || !currentConversation?._id || messages.length === 0) {
      return;
    }

    // Clear any existing scroll timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Proper delay for messages with suggestions - wait for profile loading
    const hasAIBotMessages = messages.some(m => m.suggestions && m.suggestions.length > 0);
    const scrollDelay = hasAIBotMessages ? 800 : 100; // Increased delay for suggestion loading
    
    scrollTimeout.current = setTimeout(() => {
      let scrollTarget = null;
      let scrollBehavior = "auto";
      let scrollBlock = "start";
      
      // Try to find the last read message
      if (lastReadMessageId && lastReadRef.current) {
        scrollTarget = lastReadRef.current;
        
        // Smart positioning based on message position in conversation
        const lastReadIndex = messages.findIndex(m => m._id === lastReadMessageId);
        const totalMessages = messages.length;
        
        if (lastReadIndex < 3) {
          // If last read is near the top, scroll to start to show beginning of conversation
          scrollBlock = "start";
        } else if (lastReadIndex > totalMessages - 4) {
          // If last read is near the bottom, scroll to end to show latest messages
          scrollTarget = endOfMessagesRef.current;
          scrollBlock = "end"; 
        } else {
          // If in middle, center the last read message
          scrollBlock = "center";
        }
      } else {
        // Fallback: scroll to bottom for new conversations
        scrollTarget = endOfMessagesRef.current;
        scrollBlock = "end";
      }
      
      if (scrollTarget) {
        try {
          // Use more robust scrolling with error handling
          scrollTarget.scrollIntoView({
            behavior: scrollBehavior,
            block: scrollBlock,
            inline: "nearest"
          });
          
          initialScrollDone.current = true;
          
          // Set up ResizeObserver to handle layout shifts from loading suggestions
          if (hasAIBotMessages && containerRef.current) {
            const container = containerRef.current;
            lastKnownHeight.current = container.scrollHeight;
            
            if (resizeObserver.current) {
              resizeObserver.current.disconnect();
            }
            
            resizeObserver.current = new ResizeObserver((entries) => {
              for (const entry of entries) {
                const newHeight = entry.target.scrollHeight;
                if (newHeight > lastKnownHeight.current) {
                  // Content expanded, always scroll to bottom for suggestions
                  container.scrollTop = container.scrollHeight;
                  lastKnownHeight.current = newHeight;
                }
              }
            });
            
            resizeObserver.current.observe(container);
            
            // Clean up observer after suggestions should be loaded
            setTimeout(() => {
              if (resizeObserver.current) {
                resizeObserver.current.disconnect();
                resizeObserver.current = null;
              }
            }, 5000);
          }
          
          // Additional fallback for AI bot messages: ensure we can see the suggestions
          if (hasAIBotMessages && scrollBlock === "center") {
            setTimeout(() => {
              // Slight adjustment to ensure suggestions are visible
              scrollTarget.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest"
              });
            }, 200);
          }
        } catch (error) {
          // Fallback to container scroll
          if (containerRef.current) {
            containerRef.current.scrollTop = scrollTarget === endOfMessagesRef.current 
              ? containerRef.current.scrollHeight 
              : Math.max(0, scrollTarget.offsetTop - 100);
          }
          initialScrollDone.current = true;
        }
      }
    }, scrollDelay);

    // Cleanup timeout and observer on unmount
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = null;
      }
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = null;
      }
    };
  }, [messages.length, lastReadMessageId, currentConversation?._id]);
  
  // Filter out current user and validate timestamps
  const now = Date.now();
  const TYPING_TIMEOUT = 6000; // 6 seconds
  
  const otherTypingUsers = Object.keys(currentTypingUsers).filter(userId => {
    const isNotCurrentUser = userId !== user.userId;
    const isRecent = (now - currentTypingUsers[userId]) < TYPING_TIMEOUT;
    return isNotCurrentUser && isRecent;
  });
  
  const activeBots = Object.keys(currentTypingBots).filter(botId => {
    const botTimestamp = currentTypingBots[botId];
    const botTime = typeof botTimestamp === 'string' ? new Date(botTimestamp).getTime() : botTimestamp;
    const isRecent = (now - botTime) < TYPING_TIMEOUT;
    return isRecent;
  });

  // Scroll to bottom when new message arrives (but not on initial load)

  // useEffect(() => {
  //   if (initialScrollDone.current && endOfMessagesRef.current && containerRef.current) {
  //     const container = containerRef.current;
  //     const { scrollTop, scrollHeight, clientHeight } = container;
  //     const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      
  //     // Only auto-scroll if user was near bottom (within 100px)
      
  //     if (distanceFromBottom < 100) {
  //       const lastMessage = messages[messages.length - 1];
  //       const hasSuggestions = lastMessage?.suggestions?.length > 0;
  //       const isBotMessage = lastMessage?.sender === currentConversation?.communityId;
        
  //       if (hasSuggestions) {
  //         // For suggestion messages, use multiple timed scroll attempts to handle dynamic content loading
  //         setTimeout(() => container.scrollTop = container.scrollHeight, 800);
  //         setTimeout(() => container.scrollTop = container.scrollHeight, 1500);
  //         setTimeout(() => container.scrollTop = container.scrollHeight, 3000);
          
  //         // Periodic scrolling for suggestions that load content dynamically
  //         const aggressiveScrollInterval = setInterval(() => {
  //           const currentBottom = container.scrollTop + container.clientHeight;
  //           const totalHeight = container.scrollHeight;
  //           const distanceFromBottom = totalHeight - currentBottom;
            
  //           if (distanceFromBottom > 50) {
  //             container.scrollTop = container.scrollHeight;
  //           } else {
  //             clearInterval(aggressiveScrollInterval);
  //           }
  //         }, 500);
          
  //         // Stop aggressive scrolling after 10 seconds max
  //         setTimeout(() => clearInterval(aggressiveScrollInterval), 10000);
  //       } else if (isBotMessage) {
  //         // For bot messages without suggestions, use direct scroll with fallback
  //         setTimeout(() => {
  //           container.scrollTop = container.scrollHeight;
  //           // Extra fallback to ensure we reach bottom
  //           setTimeout(() => container.scrollTop = container.scrollHeight, 50);
  //         }, 100);
  //       } else {
  //         // For user messages, immediate smooth scroll
  //         endOfMessagesRef.current.scrollIntoView({
  //           behavior: "smooth",
  //           block: "end",
  //         });
  //       }
  //     }
  //   }
  // }, [messages.length]);

  // Scroll to bottom when new message arrives (but not on initial load)
  useEffect(() => {
      const container = containerRef.current;
  const endEl = endOfMessagesRef.current;

  if (!initialScrollDone.current || !container || !endEl) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      
  // Only auto-scroll if user is near the bottom
  if (distanceFromBottom >= 100) return;
      
        const lastMessage = messages[messages.length - 1];
        const hasSuggestions = lastMessage?.suggestions?.length > 0;
        const isBotMessage = lastMessage?.sender === currentConversation?.communityId;

  // Helper: scroll to bottom immediately
  const scrollToBottom = () => {
    container.scrollTop = container.scrollHeight;
  };

  // Helper: scroll to bottom after a delay
  const delayedScroll = (delay) => {
    setTimeout(scrollToBottom, delay);
  };
        
        if (hasSuggestions) {
    // Dynamic suggestion messages need multiple scroll attempts
    delayedScroll(800);
    delayedScroll(1500);
    delayedScroll(3000);

    const interval = setInterval(() => {
      const bottom = container.scrollTop + container.clientHeight;
      const total = container.scrollHeight;
      const remaining = total - bottom;
            
      if (remaining > 50) {
        scrollToBottom();
            } else {
        clearInterval(interval);
            }
          }, 500);
          
    // Safety stop after 10 seconds
    setTimeout(() => clearInterval(interval), 10000);

        } else if (isBotMessage) {
    // Bot messages: slight delay for rendering
    delayedScroll(100);
    delayedScroll(150); // fallback

        } else {
    // User messages: smooth scroll
    endEl.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
  }, [messages.length]);


  // Scroll to bottom when typing indicators appear
  useEffect(() => {
    if (initialScrollDone.current) {
      const hasActiveTyping = otherTypingUsers.length > 0 || activeBots.length > 0;
      if (hasActiveTyping) {
        // Slight delay to ensure typing indicator is rendered before scrolling
        setTimeout(() => {
          if (typingIndicatorRef.current) {
            typingIndicatorRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          } else if (endOfMessagesRef.current) {
            endOfMessagesRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          }
        }, 100);
      }
    }
  }, [otherTypingUsers.length, activeBots.length, initialScrollDone]);

  // Auto-cleanup stale typing indicators (RE-ENABLED)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const CLEANUP_TIMEOUT = 8000; // 8 seconds
      
      // Check for stale user typing indicators
      Object.keys(typingUsers).forEach(conversationId => {
        const users = typingUsers[conversationId];
        if (users) {
          Object.keys(users).forEach(userId => {
            if ((now - users[userId]) > CLEANUP_TIMEOUT) {
              dispatch({
                type: "SET_USER_STOPPED_TYPING",
                payload: { userId, conversationId, timestamp: now }
              });
            }
          });
        }
      });
      
      // Check for stale bot typing indicators
      Object.keys(typingBots).forEach(conversationId => {
        const bots = typingBots[conversationId];
        if (bots) {
          Object.keys(bots).forEach(botId => {
            if ((now - bots[botId]) > CLEANUP_TIMEOUT) {
              dispatch({
                type: "SET_BOT_STOPPED_TYPING",
                payload: { botId, conversationId, timestamp: now }
              });
            }
          });
        }
      });
    }, 2000); // Check every 2 seconds
    
    return () => clearInterval(cleanupInterval);
  }, [typingUsers, typingBots, dispatch]);

  // Simple scroll detection for scroll-to-bottom button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      setShowScrollToBottom(distanceFromBottom > 100 && messages.length > 3);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages.length]);

  // Helper to get the correct profile pic for each message
  const getOtherUserPic = (message) => {
    if (message.sender === currentConversation?.communityId) {
      return community?.pics;
    } else if (message.suggestions > 0) {
      return community?.pics;
    } else {
      return otherUserDisplay?.pics;
    }
  };

  // Function to update the backend when the last message is read
  const updateLastMessageRead = async (messageId) => {
    if (currentConversation?.unReadMessages) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/messages/lastMessageViewed`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              conversationId: currentConversation._id,
              messageId: messageId,
              userId: user.userId,
            }),
          }
          
        );

        if (!response.ok) throw new Error("Failed to update last message read");
        const data = await response.json();

        // Update local state
        dispatch({
          type: "SET_CURRENT_CONVERSATION",
          payload: {
            ...currentConversation,
            unReadMessages: false,
            lastReadMessageId: messageId,
          },
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Error updating last message read:", error);
        }
      }
    }
  };

  // When the last message is rendered, mark as read if needed
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Only update if the last message is not already marked as read
      if (
        currentConversation?.unReadMessages &&
        lastReadMessageId !== lastMessage._id
      ) {
        updateLastMessageRead(lastMessage._id);
      }
    }
    // eslint-disable-next-line
  }, [messages, currentConversation, lastReadMessageId]);


  // Use stable sorting to maintain message order - don't re-sort on every render
  // Messages should already be in correct order from the backend/state management
  // Only sort if we detect messages are actually out of order
  const sortedMessages = React.useMemo(() => {
    // Check if messages are already in chronological order
    let needsSorting = false;
    for (let i = 1; i < messages.length; i++) {
      const prevTime = new Date(messages[i-1].createdAt).getTime();
      const currentTime = new Date(messages[i].createdAt).getTime();
      if (prevTime > currentTime) {
        needsSorting = true;
        break;
      }
    }
    
    if (!needsSorting) {
      return messages; // Return original array to maintain order
    }
    
    // Only sort if actually needed, and use stable sort with original index as tiebreaker
    return [...messages].sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      
      if (timeA === timeB) {
        // For identical timestamps, preserve original order using array index
        const indexA = messages.indexOf(a);
        const indexB = messages.indexOf(b);
        return indexA - indexB;
      }
      
      return timeA - timeB;
    });
  }, [messages]);

   
  useEffect(() => {
  if (containerRef.current) {
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  } 
  }, [sortedMessages.length]);

  return (
    <div className="messages-container" ref={containerRef}>
      {sortedMessages.map((m, i) => {
        const isLastRead = m._id === lastReadMessageId;
        return (
          <div
            key={m._id}
            ref={isLastRead ? lastReadRef : null}
            className={isLastRead ? "last-read-message messageElement" : "messageElement"}
          >
            <Message
              message={m}
              own={m.sender === user.userId}
              otherUserPic={getOtherUserPic(m)}
              reciverUserPic={userDisplayProfile?.pics}
              onToChatProfileForCSS={onToChatProfileForCSS}
              onToSuggestionProfileForCSS={onToSuggestionProfileForCSS}
              isGroupConversation={!!currentConversation?.isGroup}
              // A bot message's tap-to-reply buttons only work while it's
              // the latest message — once anyone replies, they're spent.
              showQuickReplies={i === sortedMessages.length - 1 && m.sender !== user.userId}
            />
          </div>
        );
      })}
      

      {/* Show typing indicators */}
      {(otherTypingUsers.length > 0 || activeBots.length > 0) && (
        <div ref={typingIndicatorRef} key={`typing-${Date.now()}`}>
          {otherTypingUsers.map(userId => (
            <TypingIndicator
              key={`user-${userId}-${currentTypingUsers[userId]}`}
              isUser={true}
              userName={otherUserDisplay?.firstName || 'Someone'}
            />
          ))}
          
          {activeBots.map(botId => (
            <TypingIndicator
              key={`bot-${botId}-${currentTypingBots[botId]}`}
              isBot={true}
              communityName={community?.communityName || 'Community'}
            />
          ))}
        </div>
      )}

      
      
      {/* Anchor always at end for scrolling to latest */}
      <div ref={endOfMessagesRef} />
      
      {/* Simple scroll to bottom button */}
      {showScrollToBottom && (
        <button
          className="scroll-to-bottom-btn"
          onClick={() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight;
              setShowScrollToBottom(false);
            }
          }}
          aria-label="Scroll to bottom"
        >
          ↓
        </button>
      )}
    </div>
  );
};

export default Messages;


// import React, { useEffect, useRef, useLayoutEffect } from "react";
// import "./NewMessages.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { useMessengerContext } from "../../hooks/useMessengerContext";
// import Message from "./CopyMessage";

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state } = useMessengerContext();
//   const { user } = useAuthContext();

//   const containerRef = useRef(null);
//   const lastReadRef = useRef(null);
//   const endOfMessagesRef = useRef(null);
//   const initialScrollDone = useRef(false);

//   const {
//     messages,
//     otherUserDisplay,
//     community,
//     userDisplayProfile,
//     currentConversation,
//     lastReadMessageId,
//   } = state;

//   // Log whenever lastReadMessageId changes
//   useEffect(() => {
//     console.log("lastReadMessageId updated:", lastReadMessageId);
//   }, [lastReadMessageId]);

//   // Scroll to last read message on initial mount
//   useLayoutEffect(() => {
//     if (
//       !initialScrollDone.current &&
//       lastReadRef.current &&
//       messages.length > 0
//     ) {
//       lastReadRef.current.scrollIntoView({
//         behavior: "auto",
//         block: "center",
//       });
//       initialScrollDone.current = true;
//     }
//   }, [messages, lastReadMessageId]);

//   // Scroll to bottom when new message arrives (but not on initial load)
//   useEffect(() => {
//     if (initialScrollDone.current && endOfMessagesRef.current) {
//       endOfMessagesRef.current.scrollIntoView({
//         behavior: "smooth",
//         block: "end",
//       });
//     }
//   }, [messages.length]);

//   // Helper to get the correct profile pic for each message
//   const getOtherUserPic = (message) => {
//     if (message.sender === currentConversation?.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   return (
//     <div className="messages-container" ref={containerRef}>
//       {messages.map((m, index) => {
//         const isLastRead = m._id === lastReadMessageId;
//         return (
//           <div
//             key={m._id}
//             ref={isLastRead ? lastReadRef : null}
//             className={isLastRead ? "last-read-message" : ""}
//           >
//             <Message
//               message={m}
//               own={m.sender === user.userId}
//               otherUserPic={getOtherUserPic(m)}
//               reciverUserPic={userDisplayProfile?.pics}
//               onToChatProfileForCSS={onToChatProfileForCSS}
//             />
//           </div>
//         );
//       })}
//       {/* Anchor always at end for scrolling to latest */}
//       <div ref={endOfMessagesRef} />
//     </div>
//   );
// };

// export default Messages;


// import React, { useEffect, useRef, useLayoutEffect } from "react";
// import "./NewMessages.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { useMessengerContext } from "../../hooks/useMessengerContext";
// import Message from "./CopyMessage";

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state } = useMessengerContext();
//   const { user } = useAuthContext();

//   const containerRef = useRef(null);
//   const lastReadRef = useRef(null);
//   const endOfMessagesRef = useRef(null);
//   const initialScrollDone = useRef(false);

//   const {
//     messages,
//     otherUserDisplay,
//     community,
//     userDisplayProfile,
//     currentConversation,
//     lastReadMessageId,
//   } = state;

//   // Scroll to last read message on initial mount
//   useLayoutEffect(() => {
//     if (
//       !initialScrollDone.current &&
//       lastReadRef.current &&
//       messages.length > 0
//     ) {
//       lastReadRef.current.scrollIntoView({
//         behavior: "auto",
//         block: "center",
//       });
//       initialScrollDone.current = true;
//     }
//   }, [messages, lastReadMessageId]);

//   // Scroll to bottom when new message arrives (but not on initial load)
//   useEffect(() => {
//     if (initialScrollDone.current && endOfMessagesRef.current) {
//       endOfMessagesRef.current.scrollIntoView({
//         behavior: "smooth",
//         block: "end",
//       });
//     }
//   }, [messages.length]);

//   // Helper to get the correct profile pic for each message
//   const getOtherUserPic = (message) => {
//     if (message.sender === currentConversation?.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   return (
//     <div className="messages-container" ref={containerRef}>
//       {messages.map((m, index) => {
//         const isLastRead = m._id === lastReadMessageId;
//         return (
//           <div
//             key={m._id}
//             ref={isLastRead ? lastReadRef : null}
//             className={isLastRead ? "last-read-message" : ""}
//           >
//             <Message
//               message={m}
//               own={m.sender === user.userId}
//               otherUserPic={getOtherUserPic(m)}
//               reciverUserPic={userDisplayProfile?.pics}
//               onToChatProfileForCSS={onToChatProfileForCSS}
//             />
//           </div>
//         );
//       })}
//       {/* Anchor always at end for scrolling to latest */}
//       <div ref={endOfMessagesRef} />
//     </div>
//   );
// };

// export default Messages;


// import React, { useEffect, useRef, useState } from "react";
// import "./NewMessages.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { useMessengerContext } from "../../hooks/useMessengerContext";
// import Message from "./CopyMessage";
// import { useLayoutEffect } from "react";

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state, dispatch } = useMessengerContext();
//   const { user } = useAuthContext();
//   const endOfMessagesRef = useRef(null);
//   const scrollContainerRef = useRef(null);
//   const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

//   const {
//     messages,
//     otherUserDisplay,
//     community,
//     userDisplayProfile,
//     currentConversation,
//     lastReadMessageId,
//   } = state;





//   // Handle manual scroll events
//   useEffect(() => {
//     const container = scrollContainerRef.current;
//     if (!container) return;

//     const handleScroll = () => {
//       const { scrollTop, scrollHeight, clientHeight } = container;
//       const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100;
//       setIsAutoScrollEnabled(isNearBottom);
//     };

//     container.addEventListener('scroll', handleScroll);
//     return () => container.removeEventListener('scroll', handleScroll);
//   }, []);

//   // Automatic scrolling logic
//   useLayoutEffect(() => {
//     if (isAutoScrollEnabled && endOfMessagesRef.current) {
//       endOfMessagesRef.current.scrollIntoView({
//         behavior: "smooth",
//         block: "nearest",
//         inline: "start"
//       });
//     }
//   }, [messages.length, isAutoScrollEnabled]);

//   // Manual scroll to last read message
//   const scrollToLastRead = () => {
//     const lastReadElement = document.getElementById('scrollRefElement');
//     if (lastReadElement) {
//       lastReadElement.scrollIntoView({
//         behavior: "smooth",
//         block: "center"
//       });
//     }
//   };

//   // Update last read message and handle scroll
//   const updateLastMessageRead = async (messageId) => {
//     if (!currentConversation?.unReadMessages) return;

//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/messages/lastMessageViewed`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({
//             conversationId: currentConversation._id,
//             messageId: messageId,
//             userId: user.userId,
//           }),
//         }
//       );

//       if (!response.ok) throw new Error("Failed to update last message read");
      
//       const data = await response.json();
//       dispatch({
//         type: "SET_CURRENT_CONVERSATION",
//         payload: {
//           ...currentConversation,
//           unReadMessages: false,
//           lastReadMessageId: data.message._id,
//         },
//       });

//       // Scroll to last read message if not auto-scrolling
//       if (!isAutoScrollEnabled) {
//         scrollToLastRead();
//       }
//     } catch (error) {
//       console.error("Error updating last message read:", error);
//     }

    


//   };

//     // getOtherUserPic
  
//   const getOtherUserPic = (message) => {
//     console.log("getOtherUserPic message:", message);
//     if (message.sender === currentConversation.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   return (
//     <div className="scrollContainer" ref={scrollContainerRef}>
//       {messages.map((m, index) => {
//         const isLastMessage = index === messages.length - 1;
//         const isLastReadMessage = m._id === lastReadMessageId;

//         return (
//           <div
//             key={m._id}
//             id={isLastReadMessage ? "scrollRefElement" : ""}
//             className="messageElement"
//           >
//             <Message
//               message={m}
//               own={m.sender === user.userId}
//               otherUserPic={getOtherUserPic(m)}
//               reciverUserPic={userDisplayProfile?.pics}
//               onToChatProfileForCSS={onToChatProfileForCSS}
//             />
//             {isLastMessage && (
//               <div 
//                 ref={endOfMessagesRef}
//                 style={{ float: "left", clear: "both" }}
//               />
//             )}
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// export default Messages;



//2025
// 
// // import React, { useEffect, useRef, useState } from "react";
// import "./NewMessages.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { useMessengerContext } from "../../hooks/useMessengerContext";
// import Message from "./CopyMessage";
// import { useLayoutEffect } from "react";

// // ...

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state, dispatch } = useMessengerContext();
//   const { user } = useAuthContext();
//   const scrollRef = useRef();

//   const {
//     messages,
//     otherUserDisplay,
//     community,
//     userDisplayProfile,
//     currentConversation,
//     lastReadMessageId,
//   } = state;

//   useEffect(() => {
//     const chatBoxMessages = document.querySelector(".chatBoxMessages");
//     if (chatBoxMessages) {
//       const observer = new MutationObserver(() => {
//         const lastMessage = chatBoxMessages.lastChild;
//         if (lastMessage) {
//           lastMessage.scrollIntoView({ behavior: "smooth" });
//         }
//       });

//       observer.observe(chatBoxMessages, { childList: true });

//       return () => observer.disconnect();
//     }
//   }, []);

//   useLayoutEffect(() => {
//     if (scrollRef.current) {
//       scrollRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
//     }
//   }, [messages.length, lastReadMessageId, messages]);

//   // useLayoutEffect(() => {
//   //   if (scrollRef.current) {
//   //     scrollRef.current.scrollIntoViewIfNeeded({ behavior: 'smooth', block: 'end' });
//   //     const scrollContainer = document.querySelector('.scrollContainer');
//   //     if (scrollContainer) {
//   //       scrollContainer.scrollTop += -1000;  // Adjust this value as needed
//   //     }
//   //   }
//   // }, [messages.length]);

//   // Function to send a POST request to update the last message read
//   // Function to send a POST request to update the last message read
//   const updateLastMessageRead = async (messageId) => {
//     if (currentConversation.unReadMessages === true) {
//       try {
//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/messages/lastMessageViewed`,
//           {
//             method: "POST",
//             headers: {
//               "Content-Type": "application/json",
//             },
//             body: JSON.stringify({
//               conversationId: currentConversation._id,
//               messageId: messageId,
//               userId: user.userId,
//             }),
//           }
//         );
//         if (!response.ok) {
//           throw new Error("Failed to update last message read");
//         }
//         const data = await response.json();
//         console.log("Update successful:", data);

//         // Create a new object for the current conversation with the updated unReadMessages property
//         const updatedCurrentConversation = {
//           ...currentConversation,
//           unReadMessages: false, // Assuming you want to mark messages as read
//           lastReadMessageId: data.message._id, // Update lastReadMessageId
//         };

//         // Dispatch the updated current conversation object using SET_CURRENT_CONVERSATION action type
//         dispatch({
//           type: "SET_CURRENT_CONVERSATION",
//           payload: updatedCurrentConversation,
//         });
//       } catch (error) {
//         console.error("Error updating last message read:", error);
//       }
//     }
//   };

//   const handleLastMessageRender = (messageId, isLast) => {
//     if (isLast) {
//       updateLastMessageRead(messageId);
//     }
//   };

//   console.log("lastReadMessageId: ", lastReadMessageId);

//   const getOtherUserPic = (message) => {
//     console.log("getOtherUserPic message:", message);
//     if (message.sender === currentConversation.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   return (
//     <div>
//       {messages.map((m, index) => {
//         const isLastMessage = index === messages.length - 1;
//         if (isLastMessage) {
//           handleLastMessageRender(m._id, isLastMessage);
//         }

//         // Check if the current message is the last read message
//         const isLastReadMessage = m._id === lastReadMessageId;
//         console.log("isLastReadMessage newmessages:", isLastReadMessage);

//         return (
//           <div
//             // Set the ref to scrollRef if this is the last read message
//             ref={isLastReadMessage ? scrollRef : null}
//             key={m._id}
//             className="messageElement"
//             id={isLastReadMessage ? "scrollRefElement" : ""}
//           >
//             <Message
//               message={m}
//               own={m.sender === user.userId}
//               otherUserPic={getOtherUserPic(m)}
//               reciverUserPic={userDisplayProfile?.pics}
//               onToChatProfileForCSS={onToChatProfileForCSS}
//             />
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// export default Messages;

/////////////////////////////

// import React, { useEffect, useRef } from 'react';
// import "./NewMessages.css";
// import { useAuthContext } from '../../hooks/useAuthContext';
// import { useMessengerContext } from '../../hooks/useMessengerContext';
// import Message from './CopyMessage';

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state, dispatch } = useMessengerContext();
//   const { user } = useAuthContext();
//   const scrollRef = useRef();

//   const { messages, otherUserDisplay, community, userDisplayProfile, currentConversation } = state;

//   // useEffect to log changes to currentConversation
//   useEffect(() => {
//     console.log('currentConversation changed:', currentConversation);
//   }, [currentConversation]);

//   // Function to send a POST request to update the last message read
//   const updateLastMessageRead = async (messageId) => {
//     if (currentConversation.unReadMessages === true) {
//       try {
//         const response = await fetch('http://localhost:4000/api/messages/lastMessageViewed', {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//           },
//           body: JSON.stringify({
//             conversationId: currentConversation._id,
//             messageId: messageId,
//             userId: user.userId,
//           }),
//         });
//         if (!response.ok) {
//           throw new Error('Failed to update last message read');
//         }
//         const data = await response.json();
//         console.log('Update successful:', data);

//         // Create a new object for the current conversation with the updated unReadMessages property
//         const updatedCurrentConversation = {
//           ...currentConversation,
//           unReadMessages: false, // Assuming you want to mark messages as read
//         };

//         // Dispatch the updated current conversation object using SET_CURRENT_CONVERSATION action type
//         dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: updatedCurrentConversation });

//         console.log('should have dispatched SET_CURRENT_CONVERSATION with updated currentConversation')
//       } catch (error) {
//         console.error('Error updating last message read:', error);
//       }
//     }
//   };

//   const handleLastMessageRender = (messageId, isLast) => {
//     if (isLast) {
//       updateLastMessageRead(messageId);
//     }
//   };

//       //SCROL REF
//       useEffect(() => {
//         if (scrollRef.current) {
//           const chatBoxMessages = scrollRef.current.closest('.chatBoxMessages');
//           if (chatBoxMessages) {
//             const elementTop = scrollRef.current.getBoundingClientRect().top;
//             const containerTop = chatBoxMessages.getBoundingClientRect().top;
//             const scrollPosition = elementTop - containerTop + chatBoxMessages.scrollTop;
//             chatBoxMessages.scrollTo({ top: scrollPosition, behavior: 'smooth' });
//           }
//         }
//       }, [messages]);

//   const getOtherUserPic = (message) => {
//     console.log('getOtherUserPic message:', message);
//     if (message.sender === currentConversation.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   return (
//     <div>
//       {messages.map((m, index) => {
//         const isLastMessage = index === messages.length - 1;
//         if (isLastMessage) {
//           handleLastMessageRender(m._id, isLastMessage);
//         }

//         return (
//           <div
//             ref={isLastMessage ? scrollRef : null}
//             key={m._id}
//             className="messageElement"
//             id={isLastMessage ? "scrollRefElement" : ""}
//           >
//             <Message
//               message={m}
//               own={m.sender === user.userId}
//               otherUserPic={getOtherUserPic(m)}
//               reciverUserPic={userDisplayProfile?.pics}
//               onToChatProfileForCSS={onToChatProfileForCSS}
//             />
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// export default Messages;

// import React, { useState, useEffect, useRef } from 'react';
// import "./NewMessages.css";
// import { format } from 'timeago.js';
// import ProfileSuggestions from './Suggestions';
// import Message from './CopyMessage';
// import { useAuthContext } from '../../hooks/useAuthContext';
// import { useMessengerContext } from '../../hooks/useMessengerContext';

// const Messages = ({ onToChatProfileForCSS }) => {
//   const { state } = useMessengerContext();
//   const { user } = useAuthContext();
//   const scrollRef = useRef();

//   const { messages, otherUserDisplay, community, userDisplayProfile, currentConversation } = state;

//   // Function to determine the correct picture URL
//   const getOtherUserPic = (message) => {
//     if (message.sender === currentConversation.communityId) {
//       return community?.pics;
//     } else if (message.suggestions > 0) {
//       return community?.pics;
//     } else {
//       return otherUserDisplay?.pics;
//     }
//   };

//   // Scroll to the last message when messages update
//   useEffect(() => {
//     if (scrollRef.current) {
//       const chatBoxMessages = scrollRef.current.closest('.chatBoxMessages');
//       if (chatBoxMessages) {
//         const elementTop = scrollRef.current.getBoundingClientRect().top;
//         const containerTop = chatBoxMessages.getBoundingClientRect().top;
//         const scrollPosition = elementTop - containerTop + chatBoxMessages.scrollTop;
//         chatBoxMessages.scrollTo({ top: scrollPosition, behavior: 'smooth' });
//       }
//     }
//   }, [messages]);

//   return (
//     <div>
//       {messages.map((m, index) => (
//         <div
//           ref={index === messages.length - 1 ? scrollRef : null}
//           key={m._id}
//           className="messageElement" // This applies the CSS class to all message elements
//           id={index === messages.length - 1 ? "scrollRefElement" : ""} // Conditionally add the ID to the last message element
//         >
//           <Message
//             message={m}
//             own={m.sender === user.userId}
//             otherUserPic={getOtherUserPic(m)}
//             receiverUserPic={userDisplayProfile?.pics}
//             onToChatProfileForCSS={onToChatProfileForCSS}
//           />
//         </div>
//       ))}
//     </div>
//   );
// };

// export default Messages;

// return (
//   <div className={own ? "message own" : "message"}>
//       <div className="messageTop">
//           <img
//               className="messageImg"
//               src= {own ? "https://static.vecteezy.com/system/resources/previews/034/222/551/non_2x/bearded-black-man-winking-smiling-2d-avatar-illustration-african-american-adult-sparkling-cartoon-character-face-portrait-friendly-flirty-guy-flat-color-user-profile-image-isolated-on-white-vector.jpg" : otherUserPic }
//               alt=""
//           />
// {/* Use dangerouslySetInnerHTML to render the HTML content */}
// <div className="messageText" dangerouslySetInnerHTML={{ __html: message.text }}></div>
//       </div>
//       <div>
//         {message.suggestions.length > 0  ?  <ProfileSuggestions userIds={message.suggestions} communityId={message.communityId} communityPic={otherUserPic} conversationId={message.conversationId}/>  : null /* <label> NO! suggestions </label> */}
//       </div>
//       <div className="messageBottom">{format(message.createdAt)}</div>
//   </div>
// );
// }

// import React from 'react';
// import "./Message.css";
// import {format} from 'timeago.js'

// const Message = ({message, own}) => {
//   return (
//     <div className={own ? "message own" : "message"}>
//         <div className="messageTop">
//             <img
//                 className="messageImg"
//                 src="https://static.vecteezy.com/system/resources/previews/034/222/551/non_2x/bearded-black-man-winking-smiling-2d-avatar-illustration-african-american-adult-sparkling-cartoon-character-face-portrait-friendly-flirty-guy-flat-color-user-profile-image-isolated-on-white-vector.jpg"
//                 alt=""
//             />
//             <p className="messageText">{message.text}</p>
//         </div>
//         <div className="messageBottom">{format(message.createdAt)}</div>
//     </div>
//   );
// }

// export default Message;

//PROFILE SUGGESTIONS:

// import React, { useState } from 'react';
// import "./Message.css";

// // Assuming you have a list of profiles like this
// const profiles = [
//   {
//     id: 1,
//     name: "John Doe",
//     bio: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
//     imageUrl: "https://example.com/john.jpg"
//   },
//   {
//     id: 2,
//     name: "Jane Doe",
//     bio: "Pellentesque habitant morbi tristique senectus et netus.",
//     imageUrl: "https://example.com/jane.jpg"
//   },
//   {
//     id: 3,
//     name: "Jim Doe",
//     bio: "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
//     imageUrl: "https://example.com/jim.jpg"
//   }
// ];

// const Message = () => {
//   const [selectedProfiles, setSelectedProfiles] = useState([]);

//   const handleCheckboxChange = (profileId) => {
//     setSelectedProfiles(prev => {
//       if (prev.includes(profileId)) {
//         return prev.filter(id => id !== profileId);
//       } else {
//         return [...prev, profileId];
//       }
//     });
//   };

//   const handleSubmit = (event) => {
//     event.preventDefault();
//     console.log("Selected Profiles: ", selectedProfiles);
//     // Here you can do whatever you need with the selected profiles
//   };

//   return (
//     <form onSubmit={handleSubmit}>
//       {profiles.map(profile => (
//         <div key={profile.id} className="profile">
//           <img className="profileImg" src={profile.imageUrl} alt={profile.name} />
//           <p className="profileBio">{profile.bio}</p>
//           <label>
//             <input
//               type="checkbox"
//               checked={selectedProfiles.includes(profile.id)}
//               onChange={() => handleCheckboxChange(profile.id)}
//             />
//             Connect with this person?
//           </label>
//         </div>
//       ))}
//       <button type="submit">Submit</button>
//     </form>
//   );
// }

// export default Message;
